import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import en from './translations/en';
import es from './translations/es';
import pt from './translations/pt';

const translations = { en, es, pt };

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
    // Get initial language from localStorage or default to 'en'
    const [language, setLanguage] = useState(() => {
        const saved = localStorage.getItem('app_language');
        return saved || 'en';
    });

    // Save language preference to localStorage
    useEffect(() => {
        localStorage.setItem('app_language', language);
    }, [language]);

    // Override de marca por tenant (white-label). Estructura:
    //   { [lang]: { 'dotted.key': 'valor' } }
    // Se usa, p.ej., para que el tenant demo muestre "IA Media Analytics" /
    // "Data Bunker Agent" en vez de los textos por defecto (CLIA). Vacío = sin override.
    const [brandOverride, setBrandOverride] = useState({});

    // Get translation by key path (e.g., 'menu.home' or 'homepage.title')
    const t = useCallback((key, params = {}) => {
        // 1) Override de marca (si el tenant lo define para este idioma/clave).
        const langOverride = brandOverride[language] || brandOverride.all;
        if (langOverride && Object.prototype.hasOwnProperty.call(langOverride, key)) {
            const ov = langOverride[key];
            if (typeof ov === 'string' && Object.keys(params).length > 0) {
                return ov.replace(/\{(\w+)\}/g, (m, p) => (params[p] !== undefined ? params[p] : m));
            }
            return ov;
        }

        const keys = key.split('.');
        let value = translations[language];

        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k];
            } else {
                // Fallback to English if key not found
                value = translations['en'];
                for (const fallbackKey of keys) {
                    if (value && typeof value === 'object' && fallbackKey in value) {
                        value = value[fallbackKey];
                    } else {
                        return key; // Return key if not found at all
                    }
                }
                break;
            }
        }

        // Replace parameters like {count} with actual values
        if (typeof value === 'string' && Object.keys(params).length > 0) {
            return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
                return params[paramKey] !== undefined ? params[paramKey] : match;
            });
        }

        return value || key;
    }, [language, brandOverride]);

    // Cycle through languages
    const toggleLanguage = useCallback(() => {
        setLanguage(prev => {
            const langs = Object.keys(translations);
            const currentIndex = langs.indexOf(prev);
            return langs[(currentIndex + 1) % langs.length];
        });
    }, []);

    // Set specific language
    const changeLanguage = useCallback((lang) => {
        if (translations[lang]) {
            setLanguage(lang);
        }
    }, []);

    const value = {
        language,
        setLanguage: changeLanguage,
        toggleLanguage,
        t,
        brandOverride,
        setBrandOverride,
        isEnglish: language === 'en',
        isSpanish: language === 'es',
        isPortuguese: language === 'pt'
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

// Custom hook to use language context
export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

// Shorthand hook for just the translation function
export const useTranslation = () => {
    const { t, language } = useLanguage();
    return { t, language };
};

export default LanguageContext;
